import { ReactNode } from "react"

interface PanelProps {
  children: ReactNode
  title?: string
  className?: string
  padded?: boolean
}

export function Panel({ children, title, className, padded = true }: PanelProps) {
  return (
    <div className={["panel", className].filter(Boolean).join(" ")}>
      {title && (
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider flex items-center gap-1.5">
            {title}
          </h3>
        </div>
      )}
      <div className={padded ? "p-3" : ""}>{children}</div>
    </div>
  )
}

interface StatProps {
  label: string
  value: string | number
  unit?: string
  status?: "nominal" | "safe" | "warn" | "danger" | "muted" | "running"
  trend?: string
  accent?: "cyan" | "blue" | "amber"
}

export function Stat({ label, value, unit, status = "muted", trend, accent = "cyan" }: StatProps) {
  const colorMap = {
    cyan: "text-cyan-400",
    blue: "text-blue-400",
    amber: "text-amber-400",
  }
  const statusMap = {
    nominal: "text-cyan-400",
    safe: "text-ok-500",
    warn: "text-amber-500",
    danger: "text-danger-500",
    muted: "text-dim",
    running: "text-cyan-400",
  }
  return (
    <div className="bg-surface border border-border rounded-tech p-2.5">
      <p className="text-[0.625rem] font-medium text-dim uppercase tracking-wider">{label}</p>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className={`text-sm font-mono font-bold ${colorMap[accent]} ${statusMap[status]}`}>
          {value}
        </span>
        {unit && <span className="text-xs text-dim mono">{unit}</span>}
      </div>
      {trend && (
        <span className="mt-0.5 inline-block text-[0.625rem] font-medium mono">
          {trend.startsWith("+") || trend.startsWith("-")
            ? trend.startsWith("-")
              ? `▼ ${trend}`
              : `▲ ${trend}`
            : trend}
        </span>
      )}
    </div>
  )
}

interface StatGridProps {
  children: ReactNode
  cols?: 1 | 2 | 3 | 4 | 5 | 6
}

export function StatGrid({ children, cols = 3 }: StatGridProps) {
  const map = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4", 5: "grid-cols-5", 6: "grid-cols-6" }
  return (
    <div className={`grid gap-3 ${map[cols]}`}>
      {children}
    </div>
  )
}

interface BadgeProps {
  children: ReactNode
  variant?: "nominal" | "safe" | "warn" | "danger" | "muted" | "running"
}

export function Badge({ children, variant = "muted" }: BadgeProps) {
  const base = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[0.625rem] font-medium"
  const variants: Record<string, string> = {
    nominal: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
    safe: "bg-ok-500/10 text-ok-500 border-ok-500/30",
    warn: "bg-amber-500/10 text-amber-500 border-amber-500/30",
    danger: "bg-danger-500/10 text-danger-500 border-danger-500/30",
    muted: "bg-surface text-dim border-border",
    running: "bg-blue-500/10 text-blue-400 border-blue-500/30 animate-pulse-slow",
  }
  return <span className={`${base} ${variants[variant]}`}>{children}</span>
}

interface StatusIndicatorProps {
  status: "online" | "nominal" | "warn" | "danger" | "offline" | "running"
  label: string
}

export function StatusIndicator({ status, label }: StatusIndicatorProps) {
  const dot = {
    online: "bg-ok-500 animate-pulse-slow",
    nominal: "bg-cyan-500 animate-pulse-slow",
    running: "bg-blue-500 animate-pulse-slow",
    warn: "bg-amber-500",
    danger: "bg-danger-500",
    offline: "bg-dim",
  }[status]
  return (
    <span className="flex items-center gap-1.5 text-[0.75rem]">
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      <span className={status === "danger" || status === "offline" ? "text-danger-400" : "text-muted"}>{label}</span>
    </span>
  )
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-label-sm font-semibold text-ink uppercase tracking-wider mb-1 flex items-center gap-1.5">
      {children}
    </h2>
  )
}
